import { FormButton, FormStep, Modal } from '@/src/components/common'
import { useImagePreviewStore } from '@/src/zustand/store'
import { useProducts } from '@hook/useProduct'
import { useUploadFile } from '@hook/useUploadFile'
import { yupResolver } from '@hookform/resolvers/yup'
import { productSchema, REQUIRED_PRODUCT_FIELDS } from '@schema/product.schema'
import { useAppSelector } from '@store/hooks'
import { getError, showToast } from '@util'
import { Box, Text } from 'grommet'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { FaArrowLeft } from 'react-icons/fa'
import * as yup from 'yup'
import GeneralInfoStep from '../CreateProduct/GeneralInfoStep'
import PricingStep from '../CreateProduct/PricingStep'

type Step = 1 | 2

type ProductFormData = yup.InferType<typeof productSchema>

type Props = {
  onClose: () => void
  defaultData?: ProductFormData
  id?: string
}

const EditProduct = ({ onClose, defaultData, id }: Props) => {
  const [step, setStep] = useState<Step>(1)
  const { imgFile, setImgFile } = useImagePreviewStore()
  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  // Create an object URL when imgFile is a File/Blob, and revoke on change/unmount

  const { user } = useAppSelector(state => state.user)
  const { updateProduct } = useProducts()
  const { uploadFile, isUploading } = useUploadFile()

  const initialValues = useMemo(() => {
    if (!defaultData) return {}

    return {
      brand_name: defaultData.brand_name ?? '',
      cold_chain: defaultData.cold_chain ?? false,
      controlled_substance: defaultData.controlled_substance ?? false,
      dosage_form: defaultData.dosage_form ?? '',
      generic_name: defaultData.generic_name ?? '',
      is_active: defaultData.is_active ?? true,
      mark_up_percentage: defaultData.mark_up_percentage ?? '',
      max_temp: defaultData.max_temp ?? '',
      min_temp: defaultData.min_temp ?? '',
      product_image: defaultData.product_image ?? undefined,
      route: defaultData.route ?? '',
      storage_condition: defaultData.storage_condition ?? '',
      therapeutic_category: defaultData.therapeutic_category ?? '',
      vaccine: defaultData.vaccine ?? false,
      vaccine_antigen_type: defaultData.vaccine_antigen_type ?? '',
      vaccine_presentation_type: defaultData.vaccine_presentation_type ?? '',
      vat_percentage: defaultData.vat_percentage ?? '',
      inventory_unit: defaultData.inventory_unit ?? ''
    }
  }, [defaultData])

  const methods = useForm<ProductFormData>({
    defaultValues: initialValues,
    resolver: yupResolver(productSchema)
  })

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = methods

  useEffect(() => {
    reset(initialValues)
  }, [initialValues, reset])

  const [btnType, setBtnType] = useState<'button' | 'submit'>('button')

  useEffect(() => {
    if (step === 2) {
      const timer = setTimeout(() => {
        setBtnType('submit')
      }, 500)
      return () => clearTimeout(timer)
    } else {
      setBtnType('button')
    }
  }, [step])

  const handleBack = () => {
    if (step === 1) {
      reset()
      onClose()
    } else if (step === 2) setStep(1)
  }

  const isProductFormField = () => {
    const watchValues = methods.getValues()

    // Check required fields
    for (const fieldName of REQUIRED_PRODUCT_FIELDS) {
      const value = watchValues[fieldName as keyof typeof watchValues]
      if (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value?.length === 0)
      ) {
        return false
      }
    }

    // If cold_chain is true, min_temp and max_temp must be present and not empty
    if (watchValues['cold_chain']) {
      if (
        watchValues['min_temp'] === undefined ||
        watchValues['min_temp'] === null ||
        watchValues['min_temp'] === '' ||
        watchValues['max_temp'] === undefined ||
        watchValues['max_temp'] === null ||
        watchValues['max_temp'] === ''
      ) {
        return false
      }
    }

    return true
  }
  const handleStepChange = () => {
    if (step === 1) setStep(2)
    if (!isProductFormField() && step === 2) {
      showToast('Product Fields should be filled first')
      return
    }
  }

  console.log('formdata', imgFile)

  const onSubmit = async (formData: ProductFormData) => {
    let imageSignedId: string | null = null
    if (imgFile) {
      const uploadResponse = await uploadFile(imgFile)
      imageSignedId = uploadResponse.signed_id
    }
    const {
      vaccine: vaccineValue,
      vaccine_antigen_type,
      vaccine_presentation_type,
      vat_percentage,

      mark_up_percentage,
      product_image,
      ...restAttributes
    } = formData

    const payload = {
      data: {
        attributes: {
          ...restAttributes,
          vaccine: vaccineValue,
          ...(imgFile && { product_image_signed_id: imageSignedId }),
          ...(vaccineValue && vaccine_antigen_type && { vaccine_antigen_type }),
          ...(vaccineValue && vaccine_presentation_type && { vaccine_presentation_type }),
          vat_percentage: vat_percentage !== undefined && vat_percentage !== null ? String(vat_percentage) : ''
        },
        id: id,
        relationships: {
          staff: {
            data: {
              id: `${user?.id}`,
              type: 'staff'
            }
          }
        },
        type: 'product'
      }
    }

    try {
      await updateProduct({ formVal: payload, id: id! })
      showToast('Product updated successfully')
      setImgFile(null)

      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} reset={() => reset(initialValues)} title="Edit Product">
        <form className="flex flex-col overflow-auto" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-y-4 overflow-auto p-6">
            <Box align="center" direction="row" gap={'16px'} margin={{ bottom: '32px' }}>
              <FormStep check={step > 1} label="General Information" step={1} />
              <Box pad={{ horizontal: '8px' }}>
                <Text color="#ddd">‣</Text>
              </Box>
              <FormStep check={step > 2} label="Pricing" step={2} />
            </Box>
            <Box
              style={{
                minHeight: '320px',
                overflowX: step !== 1 ? 'hidden' : undefined,
                overflowY: step !== 1 ? 'auto' : undefined,
                position: 'relative'
              }}
            >
              <GeneralInfoStep step={step} />
              <PricingStep step={step} />
            </Box>
          </div>
          <FormButton
            cancel={step === 1 ? 'Cancel' : 'Previous'}
            isLoading={isSubmitting || isUploading}
            leftIcon={step === 1 ? undefined : <FaArrowLeft />}
            onClick={() => handleStepChange()}
            onClose={handleBack}
            submit={step === 2 ? 'Save Changes' : 'Proceed'}
            type={btnType}
          />
        </form>
      </Modal>
    </FormProvider>
  )
}

export default EditProduct
